/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AddressFound.scss';

import { func, object, string } from 'prop-types';
import React, { Fragment, useState } from 'react';

import AddressDisplay from './AddressDisplay';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

const suggestedAddressOption = {
  label: 'Suggested Address',
  value: 'suggestedAddress'
};

const addressAsEnteredOption = {
  label: 'Address as Entered',
  value: 'enteredAddress'
};

const AddressDisplayWithPermissions = WithUserPermissions(AddressDisplay);

//Since we are adding another address to the modal there is a possibility that only one of the addresses is found.
//Needed to have a variation of the AddressFound modal to show No Address Found
export const NoAddressFound = ({
  header,
  headerSubText,
  address,
  name,
}) => {
  return <Fragment>
    <div className="header">{header}</div>
    <div className="header-sub-text">{headerSubText}</div>
    <div className="no-address-found">
      <AddressDisplayWithPermissions
        address={address}
        label="Address as Entered"
        name={name}
        shouldMask />
    </div>
  </Fragment>
};

export const ResidentialAddress = ({
  enteredAddress,
  header,
  headerSubText,
  name,
  selectedAddress,
  updateSelectedAddress,
  validatedAddress
}) => {

  return  <Fragment>
    <div className="header">{header}</div>
    <div className="header-sub-text">{headerSubText}</div>
    <div className="addresses">
      <AddressDisplayWithPermissions
        address={enteredAddress}
        name={name}
        onClick={() => updateSelectedAddress('enteredAddress')}
        option={addressAsEnteredOption}
        selected={selectedAddress}
        mailingType="residential"
        shouldMask />
      <AddressDisplayWithPermissions
        address={validatedAddress}
        name={name}
        onClick={() => updateSelectedAddress('suggestedAddress')}
        option={suggestedAddressOption}
        selected={selectedAddress}
        mailingType="residential"
        shouldMask />
    </div>
  </Fragment>;
};

export const MailingAddress = ({
  name,
  header,
  headerSubText,
  primaryMailingAddress,
  selectedMailingAddress,
  updateSelectedMailingAddress,
  validatedMailingAddress
}) => {
  return <Fragment>
    <div className="header">{header}</div>
    <div className="header-sub-text">{headerSubText}</div>
    <div className="addresses">
      <AddressDisplayWithPermissions
        address={primaryMailingAddress}
        name={name}
        onClick={() => updateSelectedMailingAddress('enteredAddress')}
        option={addressAsEnteredOption}
        selected={selectedMailingAddress}
        mailingType="mailing"
        shouldMask />
      <AddressDisplayWithPermissions
        address={validatedMailingAddress}
        name={name}
        onClick={() => updateSelectedMailingAddress('suggestedAddress')}
        option={suggestedAddressOption}
        selected={selectedMailingAddress}
        mailingType="mailing"
        shouldMask />
    </div>
  </Fragment>;
};

const AddressFound = ({
  enteredAddress,
  name,
  onClose,
  onSelect,
  addressFound,
  mailingAddressFound,
  validatedAddress,
  updatedContactInformation,
  hasResidential,
  validatedMailingAddress,
  residentialAddressDifferent,
  mailingAddressDifferent,
  isMailingSameAsResidential
}) => {
  const [selectedAddress, updateSelectedAddress] = useState('suggestedAddress');
  const [selectedMailingAddress, updateSelectedMailingAddress] = useState('suggestedAddress');
  const { primaryMailingAddress } = updatedContactInformation;

  const onClickUseSelectedAddress = () => {
    let address;

    if(hasResidential) {
      if(!residentialAddressDifferent) {
        address = enteredAddress;
      } else {
        address = selectedAddress === 'suggestedAddress'
          ? validatedAddress
          : enteredAddress;
      }
    } else {
      address =
        selectedAddress === 'suggestedAddress'
          ? validatedAddress
          : enteredAddress;
    }

    const addressWithDisposition = {
      ...address,
      typeId: enteredAddress.typeId,
      contactId: enteredAddress.contactId,
      country: enteredAddress.country,
      disposition: {
        action: 'update'
      }
    };

    let contactInformation;
    if(hasResidential) {
      let mailingAddress;

      if(!mailingAddressDifferent) {
        mailingAddress = primaryMailingAddress;
      } else if(isMailingSameAsResidential) {
        mailingAddress = address;
      } else if(selectedMailingAddress === 'suggestedAddress') {
        mailingAddress = validatedMailingAddress;
      } else {
        mailingAddress = primaryMailingAddress
      }

      const mailingAddressWithDisposition = {
        ...mailingAddress,
        typeId: primaryMailingAddress.typeId,
        contactId: primaryMailingAddress.contactId,
        country: primaryMailingAddress.country,
        disposition: {
          action: 'update'
        }
      };
      contactInformation = {
        ...updatedContactInformation,
        primaryMailingAddress: mailingAddressWithDisposition,
        residentialAddress: addressWithDisposition
      };
    } else {
      contactInformation = {
        ...updatedContactInformation,
        primaryMailingAddress: addressWithDisposition
      };
    }
    onSelect(contactInformation);
  };

  return (
    <div className="address-found-container">
      {hasResidential
        ? <Fragment>
          {isMailingSameAsResidential
            ? <ResidentialAddress
                enteredAddress={enteredAddress}
                header={'Residential/Legal Address & Mailing Address'}
                headerSubText={'The U.S. Postal Service suggests a modified address.'}
                name={name}
                selectedAddress={selectedAddress}
                updateSelectedAddress={updateSelectedAddress}
                validatedAddress={validatedAddress} />
            : <Fragment>
              {residentialAddressDifferent && !enteredAddress.foreign &&
                <ResidentialAddress
                  enteredAddress={enteredAddress}
                  header={'Residential/Legal Address'}
                  headerSubText={'The U.S. Postal Service suggests a modified residential/legal address.'}
                  name={name}
                  selectedAddress={selectedAddress}
                  updateSelectedAddress={updateSelectedAddress}
                  validatedAddress={validatedAddress} />
              }
              {!addressFound && !enteredAddress.foreign &&
                <NoAddressFound
                  header={'Residential/Legal Address'}
                  headerSubText={'The U.S. Postal Service was unable to verify your residential/legal address.'}
                  name={name}
                  address={enteredAddress} />
              }
              {mailingAddressDifferent && !primaryMailingAddress.foreign &&
                <MailingAddress
                  name={name}
                  header={'Mailing Address'}
                  headerSubText={'The U.S. Postal Service suggests a modified mailing address.'}
                  primaryMailingAddress={primaryMailingAddress}
                  selectedMailingAddress={selectedMailingAddress}
                  updateSelectedMailingAddress={updateSelectedMailingAddress}
                  validatedMailingAddress={validatedMailingAddress} />
              }
              {!mailingAddressFound && !primaryMailingAddress.foreign &&
                <NoAddressFound
                  header={'Mailing Address'}
                  headerSubText={'The U.S. Postal Service was unable to verify your mailing address.'}
                  name={name}
                  address={primaryMailingAddress} />
              }
            </Fragment>
          }
        </Fragment>
        : <Fragment>
          {
            /*
              Keeping this as is to not impact the old implementation. The old implementation uses the same properties as Residential Address in the new implementation
              When we do not have residential address in the service call, this implementation will be using primaryMailingAddress from the service
            */
          }
          <div className="header">Residential Address</div>
          <div className="header-sub-text">The U.S. Postal Service suggests a modified mailing address.</div>
          <div className="addresses">
            <AddressDisplayWithPermissions
              address={enteredAddress}
              name={name}
              onClick={() => updateSelectedAddress('enteredAddress')}
              option={addressAsEnteredOption}
              selected={selectedAddress}
              mailingType="mailing"
              shouldMask
            />
            <AddressDisplayWithPermissions
              address={validatedAddress}
              name={name}
              onClick={() => updateSelectedAddress('suggestedAddress')}
              option={suggestedAddressOption}
              selected={selectedAddress}
              mailingType="mailing"
              shouldMask
            />
          </div>
        </Fragment>
      }

      <div className="buttons">
        <button className="button" onClick={onClose}>
          Edit Address Info
        </button>
        <button
          className="button save-button"
          onClick={onClickUseSelectedAddress}>
          Use Selected Address Info
        </button>
      </div>
    </div>
  );
};

AddressFound.propTypes = {
  enteredAddress: object.isRequired,
  name: string.isRequired,
  onClose: func.isRequired,
  onSelect: func.isRequired,
  validatedAddress: object.isRequired
};

export default AddressFound;
